/**
 * Production `vue-livestore` replacement (see vite.config.js's alias). Real, persisted storage --
 * not a stub. Wraps the in-memory reactive core (inMemoryTableStore.ts) with real SQLite
 * persistence: hydrates each table from SQLite on startup, write-through-persists every commit in
 * the background via the real @livestore/livestore query builders schema.ts already defines
 * (engine.ts + capacitorDriver.ts). Every feature composable's `useStore()`/`store.commit()`/
 * `store.query()`/`useQuery()` call keeps working unchanged.
 */
import { defineComponent, type Ref } from 'vue';
import { tables } from '@/livestore/schema.js';
import { createEngine, type SqlQueryLike } from './engine.js';
import { capacitorDriver } from './capacitorDriver.js';
import { createInMemoryStore, type StoreEvent } from './inMemoryTableStore.js';
import { useToast } from '@/core/useToast.js';

const engine = createEngine(capacitorDriver);
const tableNames = Object.keys(tables);

// Commits must run one at a time against the single shared SQLite connection -- each commit()
// wraps its own statements correctly, but nothing serialized separate commit() calls against each
// other. Two events firing close together (e.g. first-run creating a default workplace
// immediately followed by the first time entry) started overlapping transactions on the same
// connection; jeep-sqlite/sql.js can't nest a BEGIN inside an already-open one and threw "cannot
// start a transaction within a transaction" -- silently swallowed by the .catch() below, so the
// write never actually reached SQLite even though the in-memory store already showed it applied.
// Reproduced live: an entry visible immediately after clocking in was gone after a page reload.
let writeQueue: Promise<void> = Promise.resolve();
function queuePersist(event: StoreEvent): void {
  writeQueue = writeQueue
    .then(() => engine.commit(event))
    .catch(err => {
      console.error(`Failed to persist event '${event.name}' to SQLite:`, err);
      // A commit that throws here means the in-memory store already shows the change applied,
      // but it never reached disk -- reload silently loses it. That combination (console-only
      // error + no user-facing signal) is exactly how a real bug (a schema migration gap) went
      // unnoticed as "I can save a workplace but time entries don't persist" -- surface it.
      useToast().show(
        `Couldn't save your last change (${event.name}). Please reload and try again — if this keeps happening, your local data may be out of date.`,
        { duration: 8000, variant: 'error' },
      );
    });
}

const { hydrated, hydration, store, useQuery } = createInMemoryStore(tableNames, {
  hydrate: name =>
    engine.query((tables as Record<string, { select(): SqlQueryLike }>)[name].select()),
  persist: queuePersist,
});

export const useStore = () => ({ store });
export { useQuery };
export const useClientDocument = <T = unknown>(_table: unknown): Ref<T | null> =>
  ({ value: null }) as Ref<T | null>;

export const LiveStoreProvider = defineComponent({
  setup(_, { slots }) {
    return () => (hydrated.value ? slots.default?.() : (slots.loading?.() ?? null));
  },
});

/** Exposed for App.vue to await before treating the store as ready, if it ever needs to. */
export const ready = hydration;
